import React, { useEffect, useState } from 'react';
import {
    Grid,
    Typography,
    MenuItem,
    SelectChangeEvent,
    useAppSelector,
    Icon,
    useTheme,
    useAppDispatch,
    RootState,
    SortDirections,
    ITableState,
    PargodaTagsEditor,
} from '../../../base';
import {
    convertIsoDateToDDMMYYYY,
    EMPTY_VALUE,
    getUserDisplayName,
    ICandidateMetrics,
    IJob,
    IJobMetadata,
    IStatusOption,
    JobStatus,
    TableIds,
} from '../../../common';
import {
    IconCircle,
    MetadataContainer,
    MetadataItem,
    MetricItem,
    MetricsContainer,
    StyledIconButton,
    StyledSelect,
    TextContainer,
    VerticalDivider,
} from './JobEntityHeader.styles';
import {
    jobsSelectors,
    tablesActions,
    tablesSelectors,
} from '../../../actions';
import { useStatusMenu } from './Hooks/useStatusMenu';
import { useTranslation } from 'react-i18next';
import ShareJobModal from '../../ShareJobModal';
import { useNavigate } from 'react-router-dom';
import { RoutePaths } from '../../../routes/RoutePaths';

export const JobEntityHeader = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const job: IJob =
        useAppSelector(jobsSelectors.getActiveJob) || ({} as IJob);
    const { onMenuItemClick } = useStatusMenu();
    const navigate = useNavigate();

    const jobApplicationtableState = useAppSelector((state: RootState) =>
        tablesSelectors.getTablesState(state, TableIds.JOB_APPLICATIONS)
    );

    const [status, setStatus] = React.useState('');
    const [shareModalOpen, setShareModalOpen] = useState(false);

    const onClickCandidatesButton = (status: string) => {
        const baseUrl = `${RoutePaths.jobApplications}`;
        const newTableState: ITableState = {
            ...jobApplicationtableState,
            filters: { job_id: [String(job.id)] },
            sort: { sortKey: 'id', sortDirection: SortDirections.desc },
            search: '',
        };
        switch (status) {
            case 'total':
                break;
            case 'new':
                newTableState.filters = { job_id: [job.id], status: ['NEW'] };
                break;
            case 'proceeded':
                newTableState.filters = {
                    job_id: [job.id],
                    status: ['IN_REVIEW'],
                };
                break;
            case 'declined':
                newTableState.filters = {
                    job_id: [job.id],
                    status: ['DECLINED'],
                };
                break;
            default:
                break;
        }

        dispatch(
            tablesActions.setTableStateById({
                tableId: TableIds.JOB_APPLICATIONS,
                tableState: newTableState,
            })
        );

        navigate(`${baseUrl}`);
    };

    const metrics: Array<ICandidateMetrics> = [
        {
            label: t('positionPage.candidates'),
            value: job.statistics?.totalApplicants ?? 0,
            onClick: () => onClickCandidatesButton('total'),
            iconName: 'CandidatesIcon',
        },
        {
            label: t('positionPage.uncheckedCandidates'),
            value: job.statistics?.newApplicants ?? 0,
            onClick: () => onClickCandidatesButton('new'),
            iconName: 'UncheckedCandidatesIcon',
        },
        {
            label: t('positionPage.candidatesProceeded'),
            value: job.statistics?.proceededApplicants ?? 0,
            onClick: () => onClickCandidatesButton('proceeded'),
            iconName: 'ProceededCandidatesIcon',
        },
        {
            label: t('positionPage.candidatesRejected'),
            value: job.statistics?.declinedApplicants ?? 0,
            onClick: () => onClickCandidatesButton('declined'),
            iconName: 'RejectedCandidatesIcon',
        },
    ];

    const isLegacy = job.status === JobStatus.Legacy;

    const statusOptions: Array<IStatusOption> = [
        {
            key: JobStatus.Draft,
            label: t(`statuses.${JobStatus.Draft}`),
            value: JobStatus.Draft,
            disabled: true,
        },
        {
            key: JobStatus.Published,
            label: t(`statuses.${JobStatus.Published}`),
            value: JobStatus.Published,
            disabled:
                job.status === JobStatus.Completed ||
                job.status === JobStatus.Canceled ||
                job.status === JobStatus.Published,
        },
        {
            key: JobStatus.Suspended,
            label: t(`statuses.${JobStatus.Suspended}`),
            value: JobStatus.Suspended,
            disabled:
                job.status === JobStatus.Completed ||
                job.status === JobStatus.Canceled ||
                job.status === JobStatus.Suspended,
        },
        {
            key: JobStatus.Completed,
            label: t(`statuses.${JobStatus.Completed}`),
            value: JobStatus.Completed,
            disabled:
                job.status === JobStatus.Completed ||
                job.status === JobStatus.Canceled ||
                job.status === JobStatus.Closed,
        },
        {
            key: JobStatus.Canceled,
            label: t(`statuses.${JobStatus.Canceled}`),
            value: JobStatus.Canceled,
            disabled:
                job.status === JobStatus.Completed ||
                job.status === JobStatus.Canceled,
        },
        {
            key: JobStatus.Legacy,
            label: t(`statuses.${JobStatus.Legacy}`),
            value: JobStatus.Legacy,
            disabled: true,
        },
    ];

    useEffect(() => {
        setStatus(job.status || '');
    }, [job.status]);

    const metadata: IJobMetadata = {
        createdBy: getUserDisplayName(job.creator) || EMPTY_VALUE,
        jobDepartment: EMPTY_VALUE, //TODO: Add Department to job object
        lastEdited: job.updatedAt
            ? convertIsoDateToDDMMYYYY(job.updatedAt?.toISOString())
            : EMPTY_VALUE,
        editedBy: EMPTY_VALUE, //TODO: Add Edited By to job object
        recruitingManager:
            getUserDisplayName(job.recruitingManager) || EMPTY_VALUE,
        recruiter: getUserDisplayName(job.recruiter) || EMPTY_VALUE,
    };

    const handleStatusChange = (event: SelectChangeEvent) => {
        onMenuItemClick(job.id, event.target.value);
    };

    return (
        <>
            <MetadataContainer>
                <MetadataItem>
                    <Typography variant="body2" fontWeight="bold">
                        {t('positionPage.createdBy')}:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {metadata.createdBy}
                    </Typography>
                </MetadataItem>
                <MetadataItem>
                    <Typography variant="body2" fontWeight="bold">
                        {t('positionPage.jobDepartment')}:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {metadata.jobDepartment}
                    </Typography>
                </MetadataItem>
                <MetadataItem>
                    <Typography variant="body2" fontWeight="bold">
                        {t('positionPage.lastEdited')}:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {metadata.lastEdited}
                    </Typography>
                </MetadataItem>
                <MetadataItem>
                    <Typography variant="body2" fontWeight="bold">
                        {t('positionPage.editedBy')}:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {metadata.editedBy}
                    </Typography>
                </MetadataItem>
                <MetadataItem>
                    <Typography variant="body2" fontWeight="bold">
                        {t('positionPage.recruitingManager')}:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {metadata.recruitingManager}
                    </Typography>
                </MetadataItem>
                <MetadataItem>
                    <Typography variant="body2" fontWeight="bold">
                        {t('positionPage.recruiter')}:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {metadata.recruiter}
                    </Typography>
                </MetadataItem>
                {job.id != null && (
                    <MetadataItem>
                        <Typography variant="body2" fontWeight="bold">
                            {t('positionPage.tags')}:
                        </Typography>
                        <PargodaTagsEditor
                            objectId={job.id}
                            modelName="job"
                            externalAppliedTags={job.tags || []}
                            disabled={isLegacy}
                        />
                    </MetadataItem>
                )}
            </MetadataContainer>
            <MetricsContainer>
                <Grid container spacing={2} alignItems="center">
                    <Grid
                        item
                        xs={12}
                        md
                        container
                        alignItems="center"
                        spacing={0}
                    >
                        <Grid
                            item
                            xs={12}
                            container
                            spacing={0}
                            alignItems="center"
                            flexWrap="wrap"
                        >
                            {metrics.map((metric, index) => (
                                <React.Fragment key={index}>
                                    <Grid item xs={12} sm={6} md="auto">
                                        <MetricItem onClick={metric.onClick}>
                                            <IconCircle>
                                                <Icon
                                                    name={metric.iconName}
                                                    size={40}
                                                />
                                            </IconCircle>
                                            <TextContainer>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow:
                                                            'ellipsis',
                                                    }}
                                                >
                                                    {metric.label}
                                                </Typography>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {metric.value}
                                                </Typography>
                                            </TextContainer>
                                        </MetricItem>
                                    </Grid>
                                    {index < metrics.length - 1 && (
                                        <Grid
                                            item
                                            xs={0}
                                            md="auto"
                                            sx={{
                                                display: {
                                                    xs: 'none',
                                                    md: 'block',
                                                },
                                            }}
                                        >
                                            <VerticalDivider
                                                orientation="vertical"
                                                flexItem
                                            />
                                        </Grid>
                                    )}
                                </React.Fragment>
                            ))}
                        </Grid>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        md="auto"
                        container
                        justifyContent="flex-end"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item>
                            <StyledSelect
                                value={status}
                                onChange={handleStatusChange}
                                displayEmpty
                                variant="outlined"
                                size="small"
                                disabled={isLegacy}
                            >
                                {statusOptions.map((option, index) => (
                                    <MenuItem
                                        key={index}
                                        value={option.value}
                                        disabled={option.disabled}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </StyledSelect>
                        </Grid>
                        <Grid item>
                            <StyledIconButton
                                onClick={() => {
                                    setShareModalOpen(true);
                                }}
                                size="small"
                                borderColor={theme.palette.grey[600]}
                                iconColor={theme.palette.grey[600]}
                                disabled={isLegacy}
                            >
                                <Icon name={'ShareButtonIcon'} size={40} />
                            </StyledIconButton>
                        </Grid>
                    </Grid>
                </Grid>
            </MetricsContainer>
            <ShareJobModal
                isModalOpen={shareModalOpen}
                setIsModalOpen={setShareModalOpen}
                modalTitle={t('positionPage.shareJob')}
                jobId={job.id}
            />
        </>
    );
};
